import BigNumber from "bignumber.js"

export function bigAdd(...args: Array<BigNumber.Value>) {
  return args.reduce((prev, cur) => {
    const _prev = isNum(prev)
    const _cur = isNum(cur)
    const bigPrev = new BigNumber(_prev)
    const bigCur = new BigNumber(_cur)
    return bigPrev.plus(bigCur)
  }) as BigNumber
}

export function bigSub(...args: Array<BigNumber.Value>) {
  return args.reduce((prev, cur) => {
    const _prev = isNum(prev)
    const _cur = isNum(cur)
    const bigPrev = new BigNumber(_prev)
    const bigCur = new BigNumber(_cur)
    return bigPrev.minus(bigCur)
  }) as BigNumber
}

export function bigMul(...args: Array<BigNumber.Value>) {
  return args.reduce((prev, cur) => {
    const _prev = isNum(prev)
    const _cur = isNum(cur)
    const bigPrev = new BigNumber(_prev)
    const bigCur = new BigNumber(_cur)
    return bigPrev.times(bigCur)
  }) as BigNumber
}

export function bigDiv(...args: Array<BigNumber.Value>) {
  return args.reduce((prev, cur) => {
    const _prev = isNum(prev)
    const _cur = isNum(cur)
    const bigPrev = new BigNumber(_prev)
    const bigCur = new BigNumber(_cur)
    return bigPrev.div(bigCur)
  }) as BigNumber
}

export function bigAbs(num: BigNumber.Value) {
  return new BigNumber(num).abs()
}

export function bigGt(a: BigNumber.Value, b: BigNumber.Value): boolean {
  return new BigNumber(a).gt(b)
}

export function bigGte(a: BigNumber.Value, b: BigNumber.Value): boolean {
  return new BigNumber(a).gte(b)
}

export function bigLt(a: BigNumber.Value, b: BigNumber.Value): boolean {
  return new BigNumber(a).lt(b)
}

export function bigLte(a: BigNumber.Value, b: BigNumber.Value): boolean {
  return new BigNumber(a).lte(b)
}

export function bigEq(a: BigNumber.Value, b: BigNumber.Value): boolean {
  return new BigNumber(a).eq(b)
}

export function isNum(num: BigNumber.Value): BigNumber.Value {
  if (!num || !Number(num)) return 0
  if (Number(num) === Infinity) return 0
  if (Number(num) === -Infinity) return 0
  return num
}

/**
 * get default decimal place depend on number value
 *
 * @export
 * @param {BigNumber.Value} n
 * @return {*}
 */
export function getDefaultDecimalPlace(n: BigNumber.Value): number {
  const num = new BigNumber(n)

  return num.gt(1e4) ? 2 : num.gt(1) ? 4 : 8
}

export function formatNumber(n: BigNumber.Value, opts: { dp?: number; max_dp?: number; fixed?: boolean; mode?: BigNumber.RoundingMode; format?: BigNumber.Format } = {}): string {
  const num = new BigNumber(n)
  const mode = opts.mode || BigNumber.ROUND_DOWN
  let dp = opts.dp || getDefaultDecimalPlace(num)

  if (opts.max_dp) {
    dp = Math.min(dp, opts.max_dp)
  }

  const formatOpts = { decimalSeparator: ".", ...(opts.format || {}) }

  return opts.fixed ? num.toFormat(dp, mode, formatOpts) : num.decimalPlaces(dp, mode).toFormat(formatOpts)
}

/**
 * change number to equal percent format
 *
 * @export
 * @param {{
 *   n: BigNumber.Value;
 *   symbol?: boolean;
 *   dp?: number;
 * }} opts
 * @return {*}
 */
export function toPercent(opts: { n: BigNumber.Value; symbol?: boolean; dp?: number }): string {
  const { dp = 2, n, symbol = false } = opts
  const bn = new BigNumber(n)
  const s = symbol ? (bn.gte(0) ? "+" : "") : ""

  return `${s}${bn.multipliedBy(100).toFixed(dp)}%`
}
